import { RewardStatus } from '@/components/transactions/types/rewards';

import { Paginator, ServerStatus } from './globalModels';

export type Rewards = Reward[];

export type RewardsReducerPropsType = {
  rewardsStatus: ServerStatus;
  redeemRewardStatus: ServerStatus;
  lastRedeemedReward?: Reward;
  redeemRewardErrorMsg?: string;
  rewards?: Paginator<Reward>;
  redeemRewardHTTPStatusCode?: number;
  // V3 Rewards
  vaultItemsRewards?: Paginator<VaultItemReward>;
  vaultItemsRewardsStatus: ServerStatus;
  vaultItemsRewardsErrorMsg?: string;
  vaultItemBalance?: VaultItemBalance;
  vaultItemBalanceStatus?: ServerStatus;
  redeemVaultItemRewardStatus?: ServerStatus;
  redeemVaultItemRewardXRequestId?: string | null;
  redeemVaultItemRewardErrorMsg?: string | null;
  redemption?: Redemption;
  redemptionDetailStatus?: ServerStatus;
};

export type RewardConfig = {
  tangocard_sku: string;
  image?: string;
};

export enum RewardTypeEnum {
  CUSTOM = 1,
  TANGO = 2,
  VOUCHER = 3,
  CASH = 4,
  PREPAID = 5,
}

export type Reward = {
  config?: RewardConfig;
  created_at: string;
  custom_redeem_limit: number | null;
  description: string;
  display_order: number;
  end_date: string | null;
  fixed_value: number | null;
  groups: number[];
  has_email_notifications: boolean;
  image: string | null;
  is_active: boolean;
  max_value: number | null;
  min_value: number | null;
  name: string;
  points: number;
  pointsWrapped: number[];
  portal_email: string | null;
  product_name: string | null;
  start_date: string;
  tango_description: string | null;
  tango_disclaimer: string | null;
  tango_short_description: string | null;
  type: RewardTypeEnum;
  uid: number;
  value_type: string | null;
};

export enum LoyaltyRedeemsType {
  'Redeem-purchase',
}

// V3 Reward models
export enum VaultItemStorageType {
  QUANTITY = 'quantity',
  CODE = 'code',
  BALANCE = 'balance',
}

export enum VaultItemVariantValueType {
  FIXED = 'fixed',
  RANGE = 'range',
}

export enum VendorType {
  PAYPAL = 'paypal',
  TANGO = 'tango',
  STRIPE = 'stripe',
  STRIPE_ACH = 'stripe_ach',
  STRIPE_CREDITS = 'stripe_credits',
  SENDOSO = 'sendoso',
  SHOPIFY = 'shopify',
  CUSTOM = 'custom',
}

export interface Variant {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  description: string;
  icon: string | null;
  image1: string | null;
  image2: string | null;
  image3: string | null;
  image4: string | null;
  is_active: boolean;
  storage_type: VaultItemStorageType;
  vault_item: number;
  total_quantity: number;
  cash_value: number | null;
  currency: string;
  external_item_identifier: string;
  quantity: number;
  value_type: VaultItemVariantValueType;
  min_value: number | null;
  max_value: number | null;
  disclaimer: string;
  type?: VendorType;
}
export interface VaultItemReward {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
  description: string;
  payment_vaults: number[];
  variants: Variant[];
  icon: string | null;
  image1: string | null;
  image2: string | null;
  image3: string | null;
  image4: string | null;
}
export interface VaultItemBalance {
  points_available: number;
  cash_available: number;
  cash_equivalent_available: number;
}

export type MonetaryRewardTableRecord = {
  id: number;
  created_at: string;
  expire_at?: string | null;
  referred_contact__full_name?: string;
  referred_contact__remote_customer_email?: string;
  status: string;
  amount: number;
  redeemed_amount: number;
  campaign__name: string;
};

export type TangoCard = {
  card_name?: string;
  card_number?: string;
  card_code?: string;
  card_pin?: string;
  redemption_url?: string;
  security_url?: string;
  security_code?: string;
  order_id?: string;
};

export enum RedemptionStatus {
  PROCESSING = 'processing',
  CANCELED = 'canceled',
  FAILED = 'failed',
  COMPLETED = 'completed',
  REVERSED_TO_LEDGER = 'reversed_to_ledger',
  REVERSED_TO_COMPANY = 'reversed_to_company',
}

export type Redemption = {
  id: number;
  status: RedemptionStatus;
  created_at: string;
  requested_amount: number;
  affiliate: number;
  payment_vault: number;
  payment_vault_name: string;
  vault_item_variant: number;
  variant_name: string;
  num_items: number;
  failed_reason?: string;
  tangocard?: TangoCard;
};

export type RedemptionTableRecord = Redemption &
  Pick<Redemption, 'id' | 'status' | 'created_at' | 'payment_vault' | 'requested_amount' | 'affiliate' | 'failed_reason'> & {
    payment_vault__name: string;
    payment_vault__currency: string;
    affiliate__remote_customer_email: string;
    vault_item_variant__name: string;
    transaction_items_count: number;
  };

export type RedemptionItemTableRecord = {
  id: number;
  created_at: string;
  ledger__type: string;
  ledger__status: string;
  ledger__used_amount: number;
  transaction__payment_vault__currency: string;
};

export type RewardStatusHistory = {
  history_id: number; // PK key
  id: number; // reward ID
  status: RewardStatus;
  created_at: string;
  history_change_reason: string;
};
